import { useState, useRef } from 'react';
import OptimizedImage from './OptimizedImage';
import './CarouselAlbum.css';

const CarouselAlbum = ({
  albums,
  currentIndex,
  onNext,
  onPrevious,
  onSelectAlbum,
  onDeleteClick,
  getAlbumProgress,
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  if (albums.length === 0) {
    return null;
  }

  const currentAlbum = albums[currentIndex];
  const { obtidas: stickersCollected, total: totalStickers } = getAlbumProgress
    ? getAlbumProgress(currentAlbum.id)
    : { obtidas: 0, total: 998 };
  const progressPercentage = totalStickers > 0
    ? Math.round((stickersCollected / totalStickers) * 100)
    : 0;
  const isFirstAlbum = currentIndex === 0;
  const isLastAlbum = currentIndex === albums.length - 1;

  const handleAlbumClick = () => {
    if (onSelectAlbum) {
      onSelectAlbum(currentAlbum.id);
    }
  };

  const handleOptionsToggle = (e) => {
    e.stopPropagation();
    setShowOptions(!showOptions);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setShowOptions(false);
    if (onDeleteClick) {
      onDeleteClick(currentAlbum.id, currentAlbum.name);
    }
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    if (!touchStartX.current || !touchStartY.current) return;

    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const deltaX = touchStartX.current - touchEndX;
    const deltaY = touchStartY.current - touchEndY;

    const minSwipeDistance = 50;
    const maxVerticalDelta = 100;

    if (Math.abs(deltaY) < maxVerticalDelta && Math.abs(deltaX) > minSwipeDistance) {
      if (deltaX > 0 && !isLastAlbum) {
        onNext();
      } else if (deltaX < 0 && !isFirstAlbum) {
        onPrevious();
      }
    }

    touchStartX.current = null;
    touchStartY.current = null;
  };

  return (
    <div className="carousel-container">
      <div className="carousel-content">
        {/* Seta esquerda */}
        <button
          className={`carousel-arrow carousel-arrow-left ${
            isFirstAlbum ? 'disabled' : ''
          }`}
          onClick={onPrevious}
          disabled={isFirstAlbum}
          aria-label="Álbum anterior"
        >
          <span className="arrow-icon">‹</span>
        </button>

        {/* Capa do álbum */}
        <div
          className="carousel-album-wrapper"
          onClick={handleAlbumClick}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className="carousel-album-cover">
            {/* Imagem do álbum */}
            <OptimizedImage
              src={require('../images/album_frente-Photoroom.png')}
              alt={currentAlbum.name}
              className="cover-image"
              loading="eager"
            />

            {/* Contador acima */}
            <div className="cover-counter">
              <span className="counter-value">{stickersCollected}/{totalStickers}</span>
            </div>

            {/* Overlay com nome */}
            <div className="cover-overlay">
              <h3 className="cover-name">{currentAlbum.name}</h3>
            </div>

            {/* Progress bar abaixo */}
            <div className="cover-progress">
              <div className="progress-bar-small">
                <div
                  className="progress-bar-fill-small"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <span className="progress-percent">{progressPercentage}%</span>
            </div>

            {/* Menu de opções */}
            <div className="cover-menu">
              <button
                className="menu-button"
                onClick={handleOptionsToggle}
                aria-label="Opções do álbum"
              >
                ⋮
              </button>
              {showOptions && (
                <div className="menu-dropdown">
                  <button
                    className="menu-item menu-item-delete"
                    onClick={handleDeleteClick}
                  >
                    <span className="menu-icon">🗑️</span>
                    <span>Excluir</span>
                  </button>
                </div>
              )}
            </div>

            {/* Efeito hover */}
            <div className="cover-hover-overlay" />
          </div>
        </div>

        {/* Seta direita */}
        <button
          className={`carousel-arrow carousel-arrow-right ${
            isLastAlbum ? 'disabled' : ''
          }`}
          onClick={onNext}
          disabled={isLastAlbum}
          aria-label="Próximo álbum"
        >
          <span className="arrow-icon">›</span>
        </button>
      </div>

      {/* Indicador de posição */}
      <div className="carousel-indicator">
        <span className="indicator-text">
          Álbum {currentIndex + 1} de {albums.length}
        </span>
      </div>

      {/* Dots de navegação (opcional) */}
      <div className="carousel-dots">
        {albums.map((_, index) => (
          <button
            key={index}
            className={`dot ${index === currentIndex ? 'active' : ''}`}
            onClick={() => {
              if (index > currentIndex) {
                for (let i = 0; i < index - currentIndex; i++) {
                  onNext();
                }
              } else if (index < currentIndex) {
                for (let i = 0; i < currentIndex - index; i++) {
                  onPrevious();
                }
              }
            }}
            aria-label={`Ir para álbum ${index + 1}`}
            aria-current={index === currentIndex ? 'page' : undefined}
          />
        ))}
      </div>
    </div>
  );
};

export default CarouselAlbum;
